import { PageBlock, PageBlockInput, Registry } from './base'
import { Apply, CortezaID, NoID } from '../../../cast'
import { Compose as ComposeAPI } from '../../../api-clients'
import { Module } from '../module'
import { Button } from './types'

const kind = 'RecordList'

interface FilterPreset {
  name: string;
  filter: unknown[];
  roles: string[];
}

enum SummaryMetric {
  Min = 'min',
  Max = 'max',
  Avg = 'avg',
  Sum = 'sum',
  EmptyCount = 'emptyCount',
  NotEmptyCount = 'notEmptyCount',
  UniqueCount = 'uniqueCount',
  Earliest = 'earliest',
  Latest = 'latest',
}

interface Summary {
  label: string;
  field: string[];
  metric: SummaryMetric;
  roles: string[];
}

export interface Options {
  moduleID: string;
  prefilter: string;
  presort: string;
  fields: unknown[];
  inlineEditFields: unknown[];
  hideHeader: boolean;
  hideAddButton: boolean;
  hideImportButton: boolean;
  hideConfigureFieldsButton: boolean;
  hideSearch: boolean;
  hidePaging: boolean;
  hideSorting: boolean;
  hideFiltering: boolean;
  hideRecordReminderButton: boolean;
  hideRecordCloneButton: boolean;
  hideRecordEditButton: boolean;
  hideRecordViewButton: boolean;
  hideRecordPermissionsButton: boolean;
  hideRecordDeleteButton: boolean;
  enableRecordPageNavigation: boolean;
  allowExport: boolean;
  perPage: number;
  recordDisplayOption: string;
  recordSelectorDisplayOption: string;
  addRecordDisplayOption: string;
  magnifyOption: string;
  searchableFields: string[];

  fullPageNavigation: boolean;
  showTotalCount: boolean;
  showDeletedRecordsOption: boolean;
  customFilterPresets: boolean;
  refreshRate: number;
  showRefresh: boolean;

  // Record-lines
  editable: boolean;
  draggable?: boolean;
  positionField?: string;
  refField?: string; // When adding a new record, prefill refField value with parent record ID
  editFields?: unknown[];
  linkToParent: boolean; // Legacy

  // Should records be opened in a new tab
  // legacy field that has been removed but we keep it for backwards compatibility
  openInNewTab: boolean;

  // Are table rows selectable
  selectable: boolean;
  selectMode: 'multi' | 'single' | 'range';

  // Ordered list of buttons to display in the block
  selectionButtons: Array<Button>;

  bulkRecordEditEnabled: boolean;
  inlineRecordEditEnabled: boolean;
  inlineRecordEditAllowAddField: boolean;
  inlineValueFiltering: boolean;
  filterPresets: FilterPreset[];
  showRecordPerPageOption: boolean;
  openRecordInEditMode: boolean;

  // Summaries
  customSummaries: boolean;
  summaries: Summary[];

  textStyles: {
    wrappedFields: Array<string>
  }

  hideActionsDropdown: boolean,
}

const defaults: Readonly<Options> = Object.freeze({
  moduleID: NoID,
  prefilter: '',
  presort: 'createdAt DESC',
  fields: [],
  inlineEditFields: [],
  hideHeader: false,
  hideAddButton: false,
  hideImportButton: false,
  hideConfigureFieldsButton: true,
  hideSearch: false,
  hidePaging: false,
  hideSorting: false,
  hideFiltering: false,
  hideRecordReminderButton: false,
  hideRecordCloneButton: false,
  hideRecordEditButton: false,
  hideRecordViewButton: false,
  hideRecordPermissionsButton: false,
  hideRecordDeleteButton: false,
  enableRecordPageNavigation: true,
  allowExport: true,
  perPage: 20,
  recordDisplayOption: 'sameTab',
  recordSelectorDisplayOption: 'sameTab',
  addRecordDisplayOption: 'sameTab',
  magnifyOption: '',
  searchableFields: [],

  fullPageNavigation: false,
  showTotalCount: true,
  showDeletedRecordsOption: false,
  customFilterPresets: false,

  editable: false,
  draggable: false,
  positionField: undefined,
  refField: undefined,
  editFields: [],

  linkToParent: false,

  openInNewTab: false,

  selectable: true,
  selectMode: 'multi',

  selectionButtons: [],
  refreshRate: 0,
  showRefresh: false,

  bulkRecordEditEnabled: true,
  inlineRecordEditEnabled: false,
  inlineRecordEditAllowAddField: false,
  inlineValueFiltering: false,
  filterPresets: [],
  showRecordPerPageOption: false,
  openRecordInEditMode: false,

  customSummaries: false,
  summaries: [],

  textStyles: {
    wrappedFields: [],
  },

  hideActionsDropdown: false,
})

export class PageBlockRecordList extends PageBlock {
  readonly kind = kind

  options: Options = { ...defaults }

  constructor (i?: PageBlockInput) {
    super(i)
    this.applyOptions(i?.options as Partial<Options>)
  }

  applyOptions (o?: Partial<Options>): void {
    if (!o) return

    Apply(this.options, o, CortezaID, 'moduleID')

    Apply(this.options, o, String,
      'prefilter',
      'presort',
      'selectMode',
      'positionField',
      'refField',
      'recordDisplayOption',
      'magnifyOption',
      'recordSelectorDisplayOption',
      'addRecordDisplayOption',
    )

    Apply(this.options, o, Number, 'perPage', 'refreshRate')

    if (o.fields) {
      this.options.fields = o.fields
    }

    if (o.searchableFields) {
      this.options.searchableFields = o.searchableFields
    }

    if (o.inlineEditFields) {
      this.options.inlineEditFields = o.inlineEditFields
    }

    if (o.filterPresets) {
      this.options.filterPresets = o.filterPresets
    }

    if (o.editFields) {
      this.options.editFields = o.editFields
    }

    if (o.openInNewTab) {
      this.options.recordDisplayOption = 'newTab'
    }

    Apply(this.options, o, Boolean,
      'hideHeader',
      'hideAddButton',
      'hideImportButton',
      'hideConfigureFieldsButton',
      'hideSearch',
      'hidePaging',
      'hideFiltering',
      'fullPageNavigation',
      'showTotalCount',
      'showDeletedRecordsOption',
      'customFilterPresets',
      'hideSorting',
      'allowExport',
      'selectable',
      'hideRecordReminderButton',
      'hideRecordCloneButton',
      'hideRecordEditButton',
      'hideRecordViewButton',
      'hideRecordPermissionsButton',
      'hideRecordDeleteButton',
      'enableRecordPageNavigation',
      'editable',
      'draggable',
      'linkToParent',
      'showRefresh',
      'bulkRecordEditEnabled',
      'inlineRecordEditEnabled',
      'inlineRecordEditAllowAddField',
      'inlineValueFiltering',
      'showRecordPerPageOption',
      'openRecordInEditMode',
      'customSummaries',
      'hideActionsDropdown',
    )

    if (o.selectionButtons) {
      this.options.selectionButtons = o.selectionButtons.map(b => new Button(b))
    }

    if (o.summaries) {
      this.options.summaries = o.summaries
    }

    if (o.textStyles) {
      this.options.textStyles = {
        ...this.options.textStyles,
        ...o.textStyles,
      }
    }
  }

  async fetch (api: ComposeAPI, recordListModule: Module, filter: {[_: string]: unknown}): Promise<object> {
    if (recordListModule.moduleID !== this.options.moduleID) {
      throw Error('Module incompatible, module mismatch')
    }

    filter.moduleID = this.options.moduleID
    filter.namespaceID = recordListModule.namespaceID

    return api
      .recordList(filter)
      .then(r => {
        const { set: records, filter } = r as { filter: object; set: object[] }
        return { records, filter }
      })
  }
}

Registry.set(kind, PageBlockRecordList)
